<script setup>
import { computed, ref } from 'vue'
import { convertLocalDatetimeToUtcIso } from '../utils/datetimeUtils.js'
import { useLotteryStore } from '../stores/lottery.js'
import { useOrgStore } from '../stores/orgStore.js'

const emit = defineEmits(['created', 'cancel'])
const lotteryStore = useLotteryStore()
const orgStore = useOrgStore()

const name = ref('')
const description = ref('')
const deadlineAt = ref('')
const ticketsPerBatch = ref(100)
const ticketPrice = ref('')
const formError = ref('')

const selectedSubOrgId = computed(() => {
  const value = Number(orgStore.activeSubOrgId)
  return Number.isInteger(value) && value > 0 ? value : null
})

const hasSelectedSubOrg = computed(() => selectedSubOrgId.value !== null)

const parseTicketPrice = (value) => {
  const normalized = String(value ?? '').trim()
  if (!normalized) {
    return { valid: false, number: null, error: 'Ticket price is required' }
  }

  const parsed = Number(normalized)
  if (!Number.isFinite(parsed)) {
    return { valid: false, number: null, error: 'Ticket price must be a valid number' }
  }

  if (parsed < 0) {
    return { valid: false, number: null, error: 'Ticket price cannot be negative' }
  }

  return { valid: true, number: parsed, error: '' }
}

const ticketPriceError = computed(() => parseTicketPrice(ticketPrice.value).error)
const showTicketPriceError = computed(() => String(ticketPrice.value ?? '').trim() !== '' && Boolean(ticketPriceError.value))

const submit = async () => {
  formError.value = ''

  if (!hasSelectedSubOrg.value) {
    formError.value = 'Please select a sub-organization before creating a lottery.'
    return
  }

  if (!name.value.trim()) {
    formError.value = 'Name is required'
    return
  }

  if (!Number.isInteger(Number(ticketsPerBatch.value)) || Number(ticketsPerBatch.value) <= 0) {
    formError.value = 'tickets_per_batch must be greater than 0'
    return
  }

  const parsedPrice = parseTicketPrice(ticketPrice.value)
  if (!parsedPrice.valid) {
    formError.value = parsedPrice.error
    return
  }

  const result = await lotteryStore.createLottery({
    name: name.value.trim(),
    description: description.value.trim() || null,
    deadline_at: convertLocalDatetimeToUtcIso(deadlineAt.value),
    tickets_per_batch: Number(ticketsPerBatch.value),
    ticket_price: parsedPrice.number,
    sub_org_id: selectedSubOrgId.value,
  })

  if (!result.success) {
    formError.value = result.error
    return
  }

  name.value = ''
  description.value = ''
  deadlineAt.value = ''
  ticketsPerBatch.value = 100
  ticketPrice.value = ''
  emit('created', result.lottery)
}
</script>

<template>
  <div class="create-lottery-form">
    <h3>Create Lottery</h3>
    <form @submit.prevent="submit">
      <label>
        Name
        <input v-model="name" type="text" required />
      </label>

      <label>
        Description
        <textarea v-model="description" rows="3" />
      </label>

      <label>
        Deadline (optional)
        <input v-model="deadlineAt" type="datetime-local" />
      </label>

      <label>
        Tickets per batch
        <input v-model.number="ticketsPerBatch" type="number" min="1" required />
      </label>

      <label>
        Ticket price
        <input
          v-model="ticketPrice"
          type="number"
          min="0"
          step="0.01"
          placeholder="e.g. 25.00"
          required
        />
        <span v-if="showTicketPriceError" class="field-error">{{ ticketPriceError }}</span>
      </label>

      <div class="actions">
        <button type="submit" :disabled="lotteryStore.loading || !hasSelectedSubOrg">
          {{ lotteryStore.loading ? 'Creating...' : 'Create' }}
        </button>
        <button type="button" class="secondary" @click="emit('cancel')">Cancel</button>
      </div>

      <p v-if="!hasSelectedSubOrg" class="error">Select a sub-organization to create a lottery.</p>

      <p v-if="formError" class="error">{{ formError }}</p>
    </form>
  </div>
</template>

<style scoped>
.create-lottery-form {
  margin-top: 1rem;
  padding: 1rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

form {
  display: grid;
  gap: 0.75rem;
}

label {
  display: grid;
  gap: 0.3rem;
  color: #111827;
}

input,
textarea {
  padding: 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #111827;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

button {
  padding: 0.55rem 0.9rem;
  border: none;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  cursor: pointer;
}

button.secondary {
  background: #6b7280;
}

.error {
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 6px;
  padding: 0.5rem;
}

.field-error {
  color: #b91c1c;
  font-size: 0.85rem;
}
</style>

